import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Warehouse, ShieldCheck, Truck, TrendingUp, Search, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const iconMap = {
  Globe: Globe,
  Warehouse: Warehouse,
  ShieldCheck: ShieldCheck,
  Truck: Truck,
  TrendingUp: TrendingUp,
  Search: Search,
};

const Services = () => {
  const processSteps = [
    { step: '01', title: 'Consultation', description: 'Discuss your requirements with our team' },
    { step: '02', title: 'Sourcing', description: 'We find the best products and suppliers' },
    { step: '03', title: 'Quality Check', description: 'Rigorous inspection of all products' },
    { step: '04', title: 'Delivery', description: 'Safe and timely delivery to your location' },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our <span className="text-slate-700">Services</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Comprehensive trading solutions designed to streamline your business operations and maximize your success in global markets.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl hover:shadow-slate-200/70 transition-all duration-300 border border-transparent hover:border-slate-200"
                >
                  <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How We Work</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our streamlined process ensures a smooth trading experience from start to finish
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((item, index) => (
              <div key={index} className="relative">
                {/* Connector line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-slate-200" />
                )}
                
                <div className="relative text-center">
                  <div className="w-20 h-20 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                    <span className="text-2xl font-bold text-slate-900">{item.step}</span>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Partner With Us?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                We go beyond traditional trading services to provide comprehensive solutions that drive your business growth.
              </p>

              <div className="space-y-4">
                {[
                  'Dedicated account managers for personalized service',
                  'Real-time order tracking and updates',
                  'Flexible payment terms for qualified buyers',
                  'Sample orders available for quality verification',
                  'After-sales support and warranty handling',
                  'Volume discounts for bulk purchases'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '24/7', label: 'Customer Support' },
                { value: '99%', label: 'On-Time Delivery' },
                { value: '<48h', label: 'Response Time' },
                { value: '100%', label: 'Quality Assured' },
              ].map((stat, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 text-center hover:bg-slate-100 transition-colors">
                  <p className="text-3xl font-bold text-slate-900 mb-2">{stat.value}</p>
                  <p className="text-slate-600 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Industries We Serve</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our diverse experience spans across multiple industries
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {[
              'Retail', 'E-commerce', 'Hospitality', 'Construction', 
              'Healthcare', 'Manufacturing', 'Agriculture', 'Automotive'
            ].map((industry) => (
              <span
                key={industry}
                className="px-6 py-3 bg-slate-800 text-slate-300 rounded-full text-sm hover:bg-slate-700 hover:text-white transition-colors cursor-default"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Let us know your requirements and our team will provide you with a customized trading solution.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 gap-2">
              Contact Us Today
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Services;
